import { createFileRoute } from '@tanstack/react-router';
import { assertPublicToolsEnabled } from '@/lib/public-tools-feature';
import { SeoLayout } from '@/components/astro/new-design-25/SEO/seo-layout';
import { seo } from '@/seo/seo';

const heroTitle = 'Dyslexia Reading Assistant';
const heroDescription =
  'Dyslexia reading assistant for academic content. Listen to research papers, textbooks with perfect pronunciation. Designed for dyslexic students & researchers.';

const featureTitle =
  'Strategic Positioning: "The Academic Accessibility Solution"';
const featureSubtitle = 'Academic Dyslexia Support';
const featureDescription =
  'We are not another basic reading app - We are the academic accessibility solution that helps:';
const featureBadge = 'Accessibility Focus';
const featureVariant = 'default';
const featureCtaText = 'Try Dyslexia Assistant';

const featureFeatures = [
  {
    title: 'Specialized Academic Support',
    description:
      'Dyslexic students succeed in higher education with specialized academic support',
  },
  {
    title: 'Effective Research Processing',
    description:
      'Graduate students with dyslexia process research papers effectively',
  },
  {
    title: 'Accessibility Compliance',
    description:
      'Academic institutions meet accessibility requirements for complex content',
  },
  {
    title: 'Language Barrier Solution',
    description:
      'International dyslexic learners overcome both language and learning barriers',
  },
];

const comparisonTableData = {
  title: 'Comparison Table',
  description:
    'Compare dyslexia reading tools and their effectiveness for academic content and learning support.',
  columns: [
    {
      header: 'Listening',
      subheader: 'Academic Dyslexia Tool',
      isHighlighted: true,
    },
    { header: 'Voice Dream Reader', isHighlighted: false },
    { header: 'Natural Reader', isHighlighted: false },
    { header: 'Basic TTS', isHighlighted: false },
  ],
  rows: [
    {
      feature: 'Academic Content',
      description:
        'Optimization and support for complex academic materials and research papers.',
      values: [
        { text: 'Optimized for research papers', icon: 'check' as const },
        { text: 'General text focus', icon: 'warning' as const },
        { text: 'Basic academic support', icon: 'warning' as const },
        { text: 'Poor academic handling', icon: 'close' as const },
      ],
    },
    {
      feature: 'Scientific Terms',
      description:
        'Accurate pronunciation of scientific, medical, and technical terminology.',
      values: [
        { text: 'Perfect pronunciation', icon: 'check' as const },
        { text: 'Often mispronounces', icon: 'warning' as const },
        { text: 'Struggles with technical terms', icon: 'close' as const },
        { text: 'Poor accuracy', icon: 'close' as const },
      ],
    },
    {
      feature: 'Citation Handling',
      description:
        'Smart handling of citations, references, and academic formatting elements.',
      values: [
        { text: 'Skips disruptive citations', icon: 'check' as const },
        { text: 'Reads everything', icon: 'close' as const },
        { text: 'Reads all citations', icon: 'close' as const },
        { text: 'No filtering', icon: 'close' as const },
      ],
    },
    {
      feature: 'Learning Support',
      description:
        'Specialized features designed to support dyslexic learning patterns.',
      values: [
        { text: 'Dual-channel highlighting', icon: 'check' as const },
        { text: 'Basic highlighting', icon: 'warning' as const },
        { text: 'Limited highlighting', icon: 'warning' as const },
        { text: 'Audio only', icon: 'close' as const },
      ],
    },
    {
      feature: 'Academic Price',
      description:
        'Pricing structure and accessibility for students and academic institutions.',
      values: [
        { text: 'Student-friendly pricing', icon: 'check' as const },
        { text: 'Expensive for students', icon: 'close' as const },
        { text: 'Moderate cost', icon: 'warning' as const },
        { text: 'Often free but limited', icon: 'warning' as const },
      ],
    },
  ],
  showIcons: true,
  ariaLabel: 'Dyslexia Reading Assistant Comparison Table',
};

const seoSections = [
  {
    title: 'Why Dyslexic Students Need Academic-Specific Reading Support',
    content:
      'While general dyslexia apps like Voice Dream Reader help with basic text, academic content presents unique challenges that require specialized support. Research papers contain complex sentence structure, technical terminology, and dense formatting that can overwhelm dyslexic readers even when converted to audio. Generic text-to-speech tools often mispronounce scientific terms, stumble over citations, and read footnotes aloud, creating the same cognitive disruption that makes traditional reading difficult for dyslexic learners.',
    contentSecondary:
      'Academic success for dyslexic students depends on accessing the same complex materials as their peers - research papers, technical textbooks, and scholarly articles - without the reading barriers that slow comprehension. Our academic dyslexia reading assistant solves this by providing clear pronunciation of scientific terminology, intelligent skipping of citations and footnotes, and organized section navigation that helps dyslexic learners focus on content rather than struggling with format. This specialized approach transforms academic reading from a barrier into an accessible learning method.',
  },
  {
    title: 'Evidence-Based Features Designed for Dyslexic Academic Success',
    content:
      'Research shows that dyslexic learners benefit from simultaneous audio and visual input, which is why our system highlights text in sync with natural-sounding narration. This dual-channel approach reduces the cognitive load of decoding text while maintaining visual connection to academic formatting. Unlike basic reading apps that simply read aloud, we provide speed control optimized for comprehension, allowing dyslexic users to find their optimal learning pace without sacrificing understanding.',
    contentSecondary:
      'The system also addresses working memory challenges common in dyslexia by providing section-based navigation and automatic bookmarking. Instead of losing place in long academic documents, dyslexic students can jump between sections, replay important passages, and maintain progress across study sessions. Users consistently report improved academic performance and reduced study stress, with testimonials noting "finally found a tool that handles scientific papers properly for dyslexic learners" and "transformed my graduate coursework."',
  },
];

export const Route = createFileRoute(
  '/_marketing/_seo/dyslexia-reading-assistant',
)({
  beforeLoad: assertPublicToolsEnabled,
  head: () => ({
    meta: [
      ...seo({
        title:
          'Dyslexia Reading Assistant - Academic Content Support for Students | Listening',
        description:
          'Dyslexia reading assistant for academic content. Listen to research papers, textbooks with perfect pronunciation. Designed for dyslexic students & researchers.',
      }),
    ],
  }),
  component: () => (
    <SeoLayout
      comparisonTableData={comparisonTableData}
      featureBadge={featureBadge}
      featureCtaText={featureCtaText}
      featureDescription={featureDescription}
      featureFeatures={featureFeatures}
      featureSubtitle={featureSubtitle}
      featureTitle={featureTitle}
      featureVariant={featureVariant}
      pageDescription={heroDescription}
      pageTitle={heroTitle}
      seoSections={seoSections}
    />
  ),
});
